import type { ProductDetail } from '../domain/models/ProductDetail';
import { normalizeBluestoneCanonical } from '@/features/plp/utils';

export const PDP_ROUTE_RE = /^\/[^/]+\/[^/]+~(\d+)\.html$/;

export function isPdpRoute(pathname: string): boolean {
  return PDP_ROUTE_RE.test(pathname);
}

export interface PdpSeo {
  title: string;
  ogTitle: string;
  description: string;
  keywords: string;
  canonical: string;
  shareUrl: string | null;
  ogImage: string | null;
  schemas: Array<{ key: string; content: string }>;
}

const COMPANY_NAME = 'BlueStone';

function getOgImageFromMediaItems(mediaItems: unknown): string | null {
  if (!mediaItems || typeof mediaItems !== 'object') return null;
  const carouselSeq = (mediaItems as { carouselSeq?: unknown }).carouselSeq;
  if (!Array.isArray(carouselSeq)) return null;

  for (const item of carouselSeq) {
    if (!item || typeof item !== 'object') continue;
    const typed = item as { type?: string; urlMap?: Record<string, string> };
    if (typed.type !== 'image' || !typed.urlMap) continue;
    const urlMap = typed.urlMap;
    const candidate =
      urlMap['1024'] ??
      urlMap['828'] ??
      urlMap['70'] ??
      Object.values(urlMap).find((v) => typeof v === 'string' && v.startsWith('http'));
    if (typeof candidate === 'string' && candidate.startsWith('http')) return candidate;
  }
  return null;
}

function schemaToContent(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function collectSchemas(source: {
  schema: unknown;
  breadCrumbListSchema: string;
  imageSchema: string;
  videoSchema: string;
}): Array<{ key: string; content: string }> {
  const entries: Array<{ key: string; content: string }> = [];
  const productSchema = schemaToContent(source.schema);
  const breadcrumbSchema = schemaToContent(source.breadCrumbListSchema);
  const imageSchema = schemaToContent(source.imageSchema);
  const videoSchema = schemaToContent(source.videoSchema);

  if (productSchema) entries.push({ key: 'product', content: productSchema });
  if (breadcrumbSchema) entries.push({ key: 'breadcrumb', content: breadcrumbSchema });
  if (imageSchema) entries.push({ key: 'image', content: imageSchema });
  if (videoSchema) entries.push({ key: 'video', content: videoSchema });
  return entries;
}

export function buildPdpSeoFromProductDetail(product: ProductDetail, pathname?: string): PdpSeo {
  const designName = product.designName || 'BlueStone';
  const canonicalSource = product.canonical || pathname || '/';
  return {
    title: `${designName} | ${COMPANY_NAME}`,
    ogTitle: designName,
    description: product.metaDescription ?? '',
    keywords: product.metaKeywords ?? '',
    canonical: normalizeBluestoneCanonical(canonicalSource),
    shareUrl: product.shareUrl ?? null,
    ogImage: getOgImageFromMediaItems(product.mediaItems),
    schemas: collectSchemas(product),
  };
}

export function extractPdpSeoFromPlain(
  pdpData: Record<string, unknown>,
  pathname?: string
): PdpSeo | null {
  const designName = typeof pdpData.designName === 'string' ? pdpData.designName : '';
  if (!designName) return null;

  const canonicalRaw = typeof pdpData.canonical === 'string' ? pdpData.canonical : pathname || '/';
  const shareUrl = typeof pdpData.shareUrl === 'string' ? pdpData.shareUrl : null;

  return {
    title: `${designName} | ${COMPANY_NAME}`,
    ogTitle: designName,
    description: typeof pdpData.metaDescription === 'string' ? pdpData.metaDescription : '',
    keywords: typeof pdpData.metaKeywords === 'string' ? pdpData.metaKeywords : '',
    canonical: normalizeBluestoneCanonical(canonicalRaw),
    shareUrl,
    ogImage: getOgImageFromMediaItems(pdpData.mediaItems),
    schemas: collectSchemas({
      schema: pdpData.schema,
      breadCrumbListSchema:
        typeof pdpData.breadCrumbListSchema === 'string' ? pdpData.breadCrumbListSchema : '',
      imageSchema: typeof pdpData.imageSchema === 'string' ? pdpData.imageSchema : '',
      videoSchema: typeof pdpData.videoSchema === 'string' ? pdpData.videoSchema : '',
    }),
  };
}

export function getPdpRobotsMeta(): string {
  const deployEnv = ((import.meta.env.VITE_DEPLOY_ENV as string) ?? '').toLowerCase().trim();
  const allowIndex = deployEnv === 'production' && import.meta.env.MODE !== 'development';
  return allowIndex ? 'index, follow' : 'noindex, nofollow';
}

export function getPdpRobotsMetaServer(): string {
  const deployEnv = (process.env.DEPLOY_ENV ?? process.env.VITE_DEPLOY_ENV ?? '').toLowerCase().trim();
  const allowIndex = deployEnv === 'production' && process.env.NODE_ENV !== 'development';
  return allowIndex ? 'index, follow' : 'noindex, nofollow';
}
